//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { ColumnInfo } from "../../../../models/custom"
import { Enums } from "../../../../models/common"
import { GetViewColumnOptions, OpView } from "../../../../models/core/op-view"
import { GridOptions, GridController, GridTypes } from "../../../common/grid/grid.controller"
import { OpEquipmentQuantity } from '../../../../models/custom/op-equipment-quantity'
import { OpEquipmentType } from '../../../../models/custom/op-equipment-type'
import { OpPackage } from "../../../../models/core/op-package"
import { OpViewColumn } from "../../../../models/core"

import { PopupHelper } from "../../../../helpers/popup-helper"
import { SequentialItemSaver, SequentialItemSaverOptions } from '../../../../helpers/sequential-item-saver'
import { TypeHelper, LocalizationHelper, WhereClauseHelper, EventsNames, ConvertHelper } from "../../../../helpers"

import { OrderConfirmService } from '../../../../services/order/order.confirm.service'
import { OrderService } from '../../../../services/order'
import { UserSessionService } from '../../../../services/user-session'

import { ColumnModel } from "../../../api/api.models"

import { ConfirmOrderOpitons, IConfirmOrder } from './confirm-order.interfaces'

import { ConfirmOrderDepositoryOpitons, IConfirmOrderDepository } from "./components/confirm-order-depository/"
import { IConfirmOrderCreateOrder, ConfirmOrderCreateOrderOptions } from './components/confirm-order-create-order/'

export class ConfirmOrderController implements ng.IComponentController, IConfirmOrder {

    // #region Consts

    private static get CONFIRM_ORDER_GRID_NAME(): string { return 'OrderConfirmationGrid' }

    // #endregion

    // #region Memebers

    private confirmCreateOrder: IConfirmOrderCreateOrder
    private confirmOrderDepositoryPopup: IConfirmOrderDepository
    private confirmOrderGridController: GridController<OpPackage>
    private continueButton: DevExpress.ui.dxButton
    private createOrderOptions: ConfirmOrderCreateOrderOptions
    private equipmentQuantity: OpEquipmentQuantity[]
    private gridOptions: GridOptions
    private id: number
    private minStates: OpEquipmentQuantity[] = []
    private options: ConfirmOrderOpitons
    private orderDepositoryOptions: ConfirmOrderDepositoryOpitons
    private currentPackages: OpPackage[] = []
    private popup: DevExpress.ui.dxPopup
    private popupOptions: DevExpress.ui.dxPopupOptions
    private toolbarOptions: DevExpress.ui.dxToolbarOptions

    private confirmationOptions: {
        title: string
        question: string
        onConfirmed: () => void
        onDeclined: () => void
    }

    // #endregion

    constructor(
        private $scope: ng.IScope,
        private userSession: UserSessionService,
        private orderService: OrderService,
        private orderConfirmService: OrderConfirmService
    ) {
        this.id = new Date().getTime()
    }

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #region Setup

    private initialize(): void {
        this.setTooblarOptions()
        this.setMainGridOptions()
        this.setPopupOptions()
        this.setConfirmationOptions()
        this.onInitialized()
    }

    private setTooblarOptions(): void {
        this.toolbarOptions = {
            items: [{
                location: 'after',
                widget: 'dxButton',
                options: {
                    text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Continue),
                    onClick: (e: any) => {
                        this.getQuantityToFillDepositoryMinimum()
                    },
                    onInitialized: (e: any) => {
                        this.continueButton = e.component
                    }
                },
            }]
        }
    }

    private isContinueButtonDisabled(): boolean {
        return this.confirmOrderGridController == null || _.size(this.confirmOrderGridController.selectedItemsIds) === 0
    }

    private setMainGridOptions(): void {
        this.orderService
            .getMainGridColumns()
            .then((defaultColumns: ColumnModel[], columns: ColumnModel[]) => {

                this.gridOptions = {
                    type: GridTypes.PAGINATED_SMALL,
                    name: ConfirmOrderController.CONFIRM_ORDER_GRID_NAME,
                    buttonsOptions: {
                        enabled: true
                    },
                    columnsDictionary: columns,
                    defaultColumnsDictionary: defaultColumns,
                    getDataSourceMethod: (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string): any => {
                        return this.getDataSource(columns, pageNbr, pageSize, customWhereClause)
                    },
                    onInitialized: (e, grid: GridController<OpPackage>) => {
                        this.confirmOrderGridController = grid
                    },
                    masterDetail: {
                        enabled: true,
                        autoExpandAll: true,
                        template: (container, options) => {

                            if (this.equipmentQuantity != null) {
                                const element: OpEquipmentQuantity = _.find(this.equipmentQuantity, (equipment: OpEquipmentQuantity) => {
                                    return equipment.referenceValue.idPackage == options.data.id
                                })
                                if (_.size(element) > 0) {
                                    element.equipmentQuantity.forEach((type: OpEquipmentType) => {
                                        container.append($('<div ><p>' + type.article.toString() + ' - ' + type.quantity + '</p></div>'));
                                    });
                                }
                            }
                        }
                    },
                    onSelectionChanged: (e: any) => this.onGridSelectionChanged()
                }
            })

    }

    private onGridSelectionChanged(): void {
        if (this.continueButton != null) {
            this.continueButton.option('disabled', this.isContinueButtonDisabled())
        }
    }

    private setPopupOptions(): void {
        this.popupOptions = PopupHelper.mergeWithDefaultOptions({
            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConfirmOrder),
            maxWidth: 800,
            maxHeight: 800,
            onInitialized: (e: any) => {
                this.popup = e.component
                this.popup.show()
            },
            onHidden: () => {
                this.gridOptions = null
                this.confirmOrderGridController = null
            }
        })
    }

    // #endregion

    // #region Grid Datasource

    private getDataSource(columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string): any {
        return this.getOrderViewColumn()
            .then((opViewColumn: OpViewColumn) => {
                return this.userSession
                    .getDataProvider()
                    .Order
                    .getOrderFilter(columns, pageNbr, pageSize, WhereClauseHelper.appendIn(customWhereClause, opViewColumn.name, Enums.PackageStatus.Ordered.toString()))
                    .then((response: any) => {

                        const responsePacks: OpPackage[] = response.objects as OpPackage[]
                        const ids: number[] = responsePacks.map((pakage: OpPackage) => pakage.idPackage)

                        return this.userSession.getDataProvider()
                            .Order
                            .getOrderEquipmentCollection(ids)
                            .then((contentList: OpEquipmentQuantity[]) => {
                                this.equipmentQuantity = contentList
                                this.confirmOrderGridController.table.repaint()
                                return this.userSession.getDataProvider()
                                    .Order
                                    .getOrderDestinationMagazine(ids)
                                    .then((packsWithlocation: OpPackage[]) => {
                                        response.objects = this.addLocationToOrders(packsWithlocation, responsePacks)
                                        this.currentPackages = responsePacks.map((pakage: OpPackage) => pakage.clone(true, true, true, true))
                                        return response
                                    })
                            })

                    })

            })
    }

    public getOrderViewColumn(): JQueryPromise<OpViewColumn> {

        return this.userSession.getDataProvider()
            .View.getActiveView(Enums.ReferenceType.IdPackage)
            .then((activeView: OpView) => {
                if (activeView != null) {
                    const options: GetViewColumnOptions = new GetViewColumnOptions(Enums.DataType.HELPER_ID_PACKAGE_STATUS)

                    options.keyColumn = activeView.getPrimaryKeyColumn()

                    return activeView.getViewColumn(options)
                }

                return null
            })
    }

    // #endregion

    // #region ConfirmOrderCreateOrder

    private initializeConfirmOrderCreateOrderPopup(source: OpPackage[] = []): any {
        const exsistingOrders: OpPackage[] = this.findOrdersToProceed()
        const updatedExsistingOrders: OpPackage[] = this.updateOrdersToSave(exsistingOrders)
        const dataSource: OpPackage[] = source != null ? [...updatedExsistingOrders, ...source] : updatedExsistingOrders

        if (this.createOrderOptions != null) {
            this.confirmCreateOrder.updateConfirmGridDataSource(dataSource)
        } else {
            this.createOrderOptions = {
                onInitialized: (confirmOrderCreateOrder: IConfirmOrderCreateOrder) => this.confirmCreateOrder = confirmOrderCreateOrder,
                gridDataSource: dataSource,
                onSaveOrder: (order: OpPackage[]) => {
                    this.saveOrders(order)
                }
            }
        }
    }

    // #endregion 

    // #region Order Life Cycle
    private updateOrdersToSave(packs: OpPackage[]): OpPackage[] {
        const loggedOperator = this.userSession.getLoggedOperator()
        return packs.map((pakage: OpPackage) => {
            const updatedOrder: OpPackage = pakage.clone(true, true, true, true)
            updatedOrder.dataList.setValue(Enums.PackageStatus.ToCompletion, Enums.DataType.HELPER_ID_PACKAGE_STATUS)
            updatedOrder.dataList.setValue(loggedOperator.idOperator, Enums.DataType.HELPER_ID_OPERATOR_CREATOR)
            updatedOrder.idPackageStatus = Enums.PackageStatus.ToCompletion
            updatedOrder.idOperatorCreator = loggedOperator.idOperator
            updatedOrder.opState = Enums.OpChangeState.Modified
            return updatedOrder
        })

    }

    //#region SequentialItemSaver
    private saveOrders(orders: OpPackage[]): void {
        const saveOptions: SequentialItemSaverOptions<OpPackage> = {
            itemsToSave: orders,

            saveDelegate: (toSave: OpPackage) => {
                if (toSave.idPackage < 0) {
                    toSave.idPackage = 0
                }
                return this.saveOrder(toSave)
            },
            finishedDelegate: () => {
                this.onFinishDelegate()
            }
        }
        new SequentialItemSaver(saveOptions).start()
    }

    private onFinishDelegate(): void {
        this.closePopups()
        this.confirmCreateOrder.refreshDataGrid()
    }

    private saveOrder(toSave: OpPackage): JQueryPromise<OpPackage> {
        return this.userSession.getDataProvider().Order
            .saveOrder(toSave)
            .then((order: OpPackage) => {
                return this.onSaveSucces(order)
            }).fail((error: any) => {
                this.onSaveFailed(toSave, error)
            })
    }

    private onSaveSucces(order: OpPackage): OpPackage {
        this.updateRow(order, Enums.ValidationStatus.Correct)
        return order
    }

    private onSaveFailed(order: OpPackage, error: any): void {
        const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
        order[this.orderConfirmService.ERROR_INFO_COLUMN_NAME] = errorMsg
        this.updateRow(order, Enums.ValidationStatus.Incorrect)
    }

    private updateRow(order: OpPackage, status: Enums.ValidationStatus): void {
        this.confirmCreateOrder.updateSavedRow(order, { validation: status })
    }

    private closePopups(): void {
        if (this.confirmOrderDepositoryPopup != null) {
            this.confirmOrderDepositoryPopup.hide()
        }
        if (this.popup != null) {
            this.popup.hide()
        }
        this.onSaved()
    }

    //#endregion

    private getQuantityToFillDepositoryMinimum(): void {
        const ordersWithUniqLocation: OpPackage[] = _.chain(this.currentPackages)
            .filter((pakage: OpPackage) => pakage.depositoryLocation != null)
            .uniq((pakage: OpPackage) => pakage.depositoryLocation.idLocation)
            .value()

        const depositoryIds: number[] = ordersWithUniqLocation.map((pakage: OpPackage) => {
            if (_.find(this.confirmOrderGridController.selectedItemsIds, (packId: number) => packId === pakage.idPackage)) {
                return pakage.depositoryLocation.idLocation
            }
        })

        const uniqOrders: number[] = ordersWithUniqLocation.map((pakage: OpPackage) => pakage.idPackage)

        this.userSession.getDataProvider()
            .Depository
            .getQuantityToFillDepositoryMinimum(depositoryIds, uniqOrders)
            .then((equipment: OpEquipmentQuantity[]) => {
                this.minStates = equipment
                const isStockout: boolean = equipment.some((equipment: OpEquipmentQuantity) => _.size(equipment.equipmentQuantity) > 0)
                if (_.size(equipment) > 0 && isStockout) {
                    this.showConfirmationPopup()
                } else {
                    this.initializeConfirmOrderCreateOrderPopup()
                }
            })
            .fail(() => {
                this.initializeConfirmOrderCreateOrderPopup()
            })
    }

    private addLocationToOrders(packsWithLocation: OpPackage[], sourceObjects: OpPackage[]): OpPackage[] {
        _.forEach(sourceObjects, (element: OpPackage, index: number) => {
            const packWithLocation: OpPackage = _.find(packsWithLocation, (pakage: OpPackage) => pakage.idPackage == element.idPackage)
            if (packWithLocation) {
                sourceObjects[index].depositoryLocation = packWithLocation.depositoryLocation
            }
        })
        return sourceObjects
    }

    // #endregions

    // #region Confirmation Popup

    private onDecline(): any {
        this.initializeConfirmOrderCreateOrderPopup()
    }

    private onConfirm(): any {
        this.initializeDepositoryPopUp()
    }

    private showConfirmationPopup(): void {
        this.$scope.$broadcast(EventsNames.OPEN_CONFIRMATION_POPUP + this.id, this.confirmationOptions.question)

    }

    private setConfirmationOptions(): void {
        this.confirmationOptions = {
            title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ReplenishmentStocks),
            question: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ReplenishmentStocksQuestion),
            onConfirmed: () => this.onConfirm(),
            onDeclined: () => this.onDecline()
        }
    }

    // #endregion

    private removeOrdersWithoutDepositoryLocation(orders: OpPackage[]): OpPackage[] {
        return orders.filter((pakage: OpPackage) => pakage.depositoryLocation == null)
    }

    // #region Depository Popup

    private findOrdersToProceed(): OpPackage[] {
        if (this.confirmOrderGridController != null && this.confirmOrderGridController.selectedItemsIds != null) {

            return this.confirmOrderGridController
                .selectedItemsIds
                .map((packageId: number) => {

                    const foundElement: OpPackage = _.find(this.currentPackages, (pakage: OpPackage) => {
                        return pakage.idPackage === packageId
                    })

                    return foundElement
                })
                .filter((pack: OpPackage) => pack != null)
        }

        return []
    }

    private getUniqOrders(orders: OpPackage[]): OpPackage[] {
        const arrayWithOutDuplicates: OpPackage[] = []

        const machedOrder = this.minStates.map((equipment: OpEquipmentQuantity) => {
            return _.find(orders, (order: OpPackage) => order.depositoryLocation.idLocation == equipment.referenceValue.idLocation)
        })
        return _.uniq(machedOrder, (order: OpPackage) => order.idPackage)
    }

    private initializeDepositoryPopUp(): void {
        const orders: OpPackage[] = this.findOrdersToProceed().filter((pakage: OpPackage) => pakage.depositoryLocation != null)
        this.removeOrdersWithoutDepositoryLocation(orders)
        const arrayWithOutDuplicates: OpPackage[] = this.getUniqOrders(orders)
        if (this.confirmOrderDepositoryPopup != null && this.orderDepositoryOptions != null) {
            this.confirmOrderDepositoryPopup.setDataSource(arrayWithOutDuplicates, this.minStates)
        } else {
            this.orderDepositoryOptions = {
                onInitialized: (odrderDepository: IConfirmOrderDepository) => {
                    this.confirmOrderDepositoryPopup = odrderDepository
                },
                onOrdersUpdated: (source: OpPackage[]) => {
                    this.initializeConfirmOrderCreateOrderPopup(source)
                },
                orders: arrayWithOutDuplicates,
                minStates: this.minStates,
            }
        }
    }

    // #endregion

    // #region Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }
    private onSaved(): void {
        if (this.options != null && _.isFunction(this.options.onSaved)) {
            this.options.onSaved()
        }
    }

    // #endregion

    // #region IConfirmOrder implementation

    public show() {
        this.setMainGridOptions()
        this.popup.show()
    }

    // #endregion

}